import { useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

type SubmitStatus = "idle" | "loading" | "success" | "error";

const PANEL_IMAGE =
  "https://public.readdy.ai/ai/img_res/3cb390a666c291aa7775ebbef3fd3638.jpg";

export default function LeadForm() {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const { ref, isVisible } = useScrollReveal();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams();

    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    setStatus("loading");
    try {
      const res = await fetch(
        "https://readdy.ai/api/form/d76l007lpdqvlupfq1pg",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: data.toString(),
        }
      );
      if (res.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="contact-form"
      className="bg-cream-DEFAULT py-20 md:py-28"
      aria-labelledby="form-heading"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        {/* Section header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4 justify-center">
            <span className="h-px w-10 bg-gold-DEFAULT" />
            <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
              GET IN TOUCH
            </span>
            <span className="h-px w-10 bg-gold-DEFAULT" />
          </div>
          <h2
            id="form-heading"
            className="font-serif text-charcoal leading-tight"
            style={{ fontSize: "clamp(1.9rem, 3vw, 2.7rem)" }}
          >
            Ready to Find Your Perfect Vehicle?
          </h2>
          <p className="text-charcoal-soft font-sans text-base mt-3 max-w-lg mx-auto">
            Send us a message and our team responds the same day. No pressure, no obligation.
          </p>
        </div>

        <div
          ref={ref}
          className={`bg-white rounded-2xl overflow-hidden transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="flex flex-col lg:flex-row">
            {/* Left info panel */}
            <div className="lg:w-5/12 relative overflow-hidden flex flex-col justify-between" style={{ minHeight: "580px" }}>
              {/* Background image */}
              <div className="absolute inset-0">
                <img
                  src={PANEL_IMAGE}
                  alt=""
                  className="w-full h-full object-cover object-center"
                />
                {/* Lighter overlay — image visible, text still readable */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/65 to-black/80" />
              </div>
              {/* Gold top line */}
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold-DEFAULT/60 to-transparent" />

              <div className="relative z-10 p-10 md:p-12">
                <div className="inline-flex items-center gap-2 mb-7">
                  <span className="h-px w-8 bg-gold-DEFAULT" />
                  <span className="text-gold-DEFAULT text-xs font-semibold tracking-[0.2em] uppercase font-sans">
                    CONTACT US
                  </span>
                </div>
                <p className="text-white/80 font-sans text-base leading-relaxed mb-10">
                  Our team is standing by to help you find the right vehicle and the right financing — no matter your credit situation.
                </p>

                <div className="flex flex-col gap-6">
                  <a
                    href="tel:9527693208"
                    className="flex items-center gap-4 hover:text-gold-DEFAULT transition-colors cursor-pointer group"
                    aria-label="Call (952) 769-3208"
                  >
                    <div className="w-11 h-11 rounded-full bg-gold-DEFAULT/15 flex items-center justify-center flex-shrink-0 group-hover:bg-gold-DEFAULT/25 transition-colors border border-gold-DEFAULT/30">
                      <i className="ri-phone-fill text-gold-DEFAULT text-lg" />
                    </div>
                    <div>
                      <p className="text-white/80 text-xs font-sans mb-0.5">Call Anytime</p>
                      <p className="text-white font-bold text-base font-sans whitespace-nowrap">
                        (952) 769-3208
                      </p>
                    </div>
                  </a>
                  <a
                    href="mailto:info@uagmn.com"
                    className="flex items-center gap-4 hover:text-gold-DEFAULT transition-colors cursor-pointer group"
                  >
                    <div className="w-11 h-11 rounded-full bg-gold-DEFAULT/15 flex items-center justify-center flex-shrink-0 group-hover:bg-gold-DEFAULT/25 transition-colors border border-gold-DEFAULT/30">
                      <i className="ri-mail-fill text-gold-DEFAULT text-lg" />
                    </div>
                    <div>
                      <p className="text-white/80 text-xs font-sans mb-0.5">Email Us</p>
                      <p className="text-white font-bold text-sm font-sans whitespace-nowrap">
                        info@uagmn.com
                      </p>
                    </div>
                  </a>
                  <a
                    href="https://maps.app.goo.gl/Vm3ubU5UPaCURLk67"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 hover:text-gold-DEFAULT transition-colors cursor-pointer group"
                  >
                    <div className="w-11 h-11 rounded-full bg-gold-DEFAULT/15 flex items-center justify-center flex-shrink-0 group-hover:bg-gold-DEFAULT/25 transition-colors border border-gold-DEFAULT/30">
                      <i className="ri-map-pin-fill text-gold-DEFAULT text-lg" />
                    </div>
                    <div>
                      <p className="text-white/80 text-xs font-sans mb-0.5">Visit Us</p>
                      <p className="text-white font-bold text-sm font-sans">
                        1024 8th St<br />Farmington, MN 55024
                      </p>
                    </div>
                  </a>
                </div>
              </div>

              <div className="relative z-10 px-10 md:px-12 pb-10 pt-6 border-t border-white/12">
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <div key={s} className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-star-fill text-gold-DEFAULT text-sm" />
                    </div>
                  ))}
                  <span className="text-gold-DEFAULT font-serif text-lg font-semibold ml-2">5.0</span>
                </div>
                <p className="text-white/82 text-sm font-sans">
                  47+ Verified Google Reviews · Farmington, MN
                </p>
              </div>
            </div>

            {/* Right form */}
            <div className="lg:w-7/12 p-10 md:p-12">
              {status === "success" ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-gold-DEFAULT/10 flex items-center justify-center mb-5">
                    <i className="ri-check-line text-gold-DEFAULT text-3xl" />
                  </div>
                  <h3 className="font-serif text-charcoal text-2xl mb-3">
                    Message Sent!
                  </h3>
                  <p className="text-charcoal-soft font-sans text-sm max-w-sm">
                    Thank you for reaching out. Our team will be in touch with
                    you shortly. For immediate assistance, call{" "}
                    <a
                      href="tel:(952)769-3208"
                      className="text-gold-DEFAULT font-semibold whitespace-nowrap"
                    >
                      (952) 769-3208
                    </a>
                    .
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  action="https://readdy.ai/api/form/d76l007lpdqvlupfq1pg"
                  method="POST"
                  className="flex flex-col gap-5 h-full justify-center"
                >
                  <div className="mb-2">
                    <h3 className="font-serif text-charcoal text-2xl mb-1">Send Us a Message</h3>
                    <p className="text-charcoal-soft font-sans text-sm">We respond within a few hours, usually much faster.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-charcoal font-sans mb-1.5 tracking-wide">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        required
                        placeholder="Your full name"
                        className="w-full border border-cream-dark rounded-lg px-4 py-3 text-sm font-sans text-charcoal placeholder-charcoal-soft/50 focus:outline-none focus:border-gold-DEFAULT transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-charcoal font-sans mb-1.5 tracking-wide">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="(xxx) xxx-xxxx"
                        className="w-full border border-cream-dark rounded-lg px-4 py-3 text-sm font-sans text-charcoal placeholder-charcoal-soft/50 focus:outline-none focus:border-gold-DEFAULT transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-charcoal font-sans mb-1.5 tracking-wide">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full border border-cream-dark rounded-lg px-4 py-3 text-sm font-sans text-charcoal placeholder-charcoal-soft/50 focus:outline-none focus:border-gold-DEFAULT transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-charcoal font-sans mb-1.5 tracking-wide">
                      I&apos;m Interested In
                    </label>
                    <select
                      name="interest"
                      className="w-full border border-cream-dark rounded-lg px-4 py-3 text-sm font-sans text-charcoal focus:outline-none focus:border-gold-DEFAULT transition-colors bg-white"
                    >
                      <option value="">Select an option</option>
                      <option value="suv">SUV / Crossover</option>
                      <option value="sedan">Sedan</option>
                      <option value="truck">Pickup Truck</option>
                      <option value="financing">Financing Options</option>
                      <option value="pre-qualified">Getting Pre-Qualified</option>
                      <option value="general">General Inquiry</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-charcoal font-sans mb-1.5 tracking-wide">
                      Message
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us what you are looking for, your budget, or any questions..."
                      className="w-full border border-cream-dark rounded-lg px-4 py-3 text-sm font-sans text-charcoal placeholder-charcoal-soft/50 focus:outline-none focus:border-gold-DEFAULT transition-colors resize-none"
                    />
                  </div>

                  {status === "error" && (
                    <p className="text-red-500 text-xs font-sans">
                      Something went wrong. Please try again or call (952) 769-3208.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="w-full bg-gold-DEFAULT text-charcoal font-bold text-sm py-4 rounded-lg hover:bg-gold-dark transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed tracking-wide"
                  >
                    {status === "loading" ? "Sending..." : "Send Message — We Respond Fast"}
                  </button>

                  <p className="text-sm text-charcoal-soft font-sans text-center">
                    Or call directly:{" "}
                    <a
                      href="tel:(952)769-3208"
                      className="text-gold-DEFAULT font-bold whitespace-nowrap"
                    >
                      (952) 769-3208
                    </a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
